/* eslint-disable camelcase -- not applicable as direcuts uses underscores for collections and fields */
import type {
  AgentResponse,
  AgentText,
  ServiceBenefitsResponse,
  ServiceBenefitsResponseBenefit,
  ServiceCenterAgentResponseAgent,
} from '@/api/directus/queries';
import type {TravelType} from '@/modules/common/context/QueryParams/models';
import type {Page} from '@/types';

import axios from 'axios';

import {agents, benefits} from '@/api/directus/queries';
import {PAGE_HOTELS, PAGE_OFFERS} from '@/consts';
import {TravelTypes} from '@/modules/common/context/QueryParams/models';
import {createDirectusAssetUrl} from '@/utils/createDirectusAssetUrl';

export type ServiceCenterBenefit = {
  id: string;
  icon: string;
  title: string;
  description?: string;
};

type ServiceCenterPhoneNumber = {
  number: string;
  devices: Array<'sm' | 'md' | 'lg'>;
  pageType: Page;
  travelTypes: Array<TravelType>;
};

type ServiceCenterAgentTextsKey = `${Page}_${TravelType}_${string}`;

type ServiceCenterAgentTexts = Record<ServiceCenterAgentTextsKey, string> & {
  offers_packageTour_default: string;
  offers_ownArrival_default: string;
  hotels_ownArrival_default: string;
  hotels_packageTour_default: string;
};

export type ServiceCenterAgent = {
  id: string;
  title: string;
  subtitle?: string;
  oldTitle: string;
  oldTitleOffers: string;
  oldSubtitle: string;
  portrait: string;
  phoneNumbers: Array<ServiceCenterPhoneNumber>;
  texts: ServiceCenterAgentTexts;
};

export type GetAgentsReturn = {
  benefits: Array<ServiceCenterBenefit>;
  agents: Array<ServiceCenterAgent>;
};

export const createAgentTextKey = (
  page: Page,
  travelType: TravelType,
  destinationId = 'default'
): ServiceCenterAgentTextsKey => {
  return `${page}_${travelType}_${destinationId}`;
};

export const extractText = ({
  texts,
  destinationId = 'default',
  page,
  travelType,
}: {
  texts: Array<AgentText>;
  page: Page;
  travelType: TravelType;
  destinationId?: string;
}) => {
  const text = texts.find(
    ({service_center_agent_texts_id}) =>
      service_center_agent_texts_id.page_type === page &&
      service_center_agent_texts_id.destination_id === destinationId &&
      service_center_agent_texts_id.travel_types?.find(
        (type) => type.travelTypes_id.value === travelType
      )
  );
  return text?.service_center_agent_texts_id.text ?? '';
};

export const benefitMapper = (
  allBenefits: Array<ServiceBenefitsResponseBenefit>
): Array<ServiceCenterBenefit> => {
  return allBenefits.length > 0
    ? allBenefits.map(({icon, ...rest}) => {
        return {
          ...rest,
          icon: createDirectusAssetUrl(icon.filename_disk),
        };
      })
    : [];
};

export const phoneNumberMapper = (
  phone_numbers: ServiceCenterAgentResponseAgent['phone_numbers']
): ServiceCenterAgent['phoneNumbers'] => {
  return phone_numbers.map(
    ({
      service_center_agent_phone_numbers_id: {
        devices,
        number,
        page_type,
        travel_types,
      },
    }) => {
      return {
        number,
        devices: devices.map((device) => device.device_id.value),
        pageType: page_type,
        travelTypes: travel_types.map((type) => type.travelTypes_id.value),
      };
    }
  );
};

export const agentMapper = (
  allAgents: Array<ServiceCenterAgentResponseAgent>,
  page: Page,
  travelType: TravelType,
  destinationId: string
): Array<ServiceCenterAgent> => {
  return allAgents.map(
    ({
      id,
      phone_numbers,
      portrait,
      title,
      subtitle,
      old_subtitle,
      old_title,
      old_title_offers,
      texts,
    }) => {
      return {
        id,
        title,
        subtitle,
        oldTitle: old_title ?? '',
        oldTitleOffers: old_title_offers ?? '',
        oldSubtitle: old_subtitle ?? '',
        portrait: createDirectusAssetUrl(portrait.filename_disk),
        phoneNumbers: phoneNumberMapper(phone_numbers),
        texts: {
          offers_packageTour_default: extractText({
            texts,
            page: PAGE_OFFERS,
            travelType: TravelTypes.packageTour,
          }),
          offers_ownArrival_default: extractText({
            texts,
            page: PAGE_OFFERS,
            travelType: TravelTypes.ownArrival,
          }),
          hotels_ownArrival_default: extractText({
            texts,
            page: PAGE_HOTELS,
            travelType: TravelTypes.ownArrival,
          }),
          hotels_packageTour_default: extractText({
            texts,
            page: PAGE_HOTELS,
            travelType: TravelTypes.packageTour,
          }),
          [createAgentTextKey(page, travelType, destinationId)]: extractText({
            texts,
            page,
            travelType,
            destinationId,
          }),
        },
      };
    }
  );
};

export const getServiceCenterAgents = async ({
  id,
  page,
  destinationId,
  travelType,
  abortSignal,
}: {
  id?: string;
  page: Page;
  destinationId: string;
  travelType: TravelType;
  abortSignal?: AbortSignal;
}): Promise<GetAgentsReturn> => {
  const response = await axios<AgentResponse & ServiceBenefitsResponse>({
    method: 'post',
    url: import.meta.env.VITE_SERVICE_DIRECTUS_URL,
    data: {
      query: `query ServiceCenterBanner {
        ${agents({
          id,
          page,
          destinationId,
          travelType,
        })}
        ${benefits()}
      }`,
    },
    signal: abortSignal,
  });

  const allAgents = response.data.data.service_center_agents;
  const allBenefits = response.data.data.service_center_benefits;

  // map response to a smaller and better usable format
  return {
    benefits: benefitMapper(allBenefits),
    agents: agentMapper(allAgents, page, travelType, destinationId),
  };
};
